import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type FC, useEffect, useState } from "react";
import React from "react";
import { Header } from "@/components/header";
import { LLMModelConfig } from "@/lib/types";
import { createLLMModelConfig, deleteLLMModelConfig, listLLMModelConfigs, updateLLMModelConfig } from "@/api/llm";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import toast from "react-hot-toast";
import { LLMConfigModal } from "@/components/llm-config-modal";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { fetchAuthSession } from "aws-amplify/auth";

export const LLMConfig: FC = () => {
  const [configs, setConfigs] = useState<LLMModelConfig[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<LLMModelConfig | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<LLMModelConfig | null>(null);
  const [token, setToken] = useState<string | undefined>(undefined);

  const fetchConfigs = async () => {
    if (!token) return;
    try {
      const data = await listLLMModelConfigs(token);
      setConfigs(data);
    } catch (error) {
      toast.error("Failed to fetch LLM model configs.");
      console.error("Failed to fetch LLM model configs:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const authSession = await fetchAuthSession();
        const maybeToken = authSession.tokens?.idToken?.toString();
        if (!maybeToken) {
          toast.error("認証情報が見つかりません。リロードして再度ログインしてください。");
          return;
        }
        setToken(maybeToken);
      } catch (error) {
        console.error("Failed to fetch auth session:", error);
        toast.error("認証に失敗しました");
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (token) {
      fetchConfigs();
    }
  }, [token]);

  const handleCreate = () => {
    setSelectedConfig(null);
    setIsModalOpen(true);
  };

  const handleEdit = (config: LLMModelConfig) => {
    setSelectedConfig(config);
    setIsModalOpen(true);
  };

  const handleDelete = (config: LLMModelConfig) => {
    setConfigToDelete(config);
    setIsConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (configToDelete && token) {
      try {
        await deleteLLMModelConfig(token, configToDelete.id);
        toast.success("LLM model config deleted successfully.");
        fetchConfigs();
      } catch (error) {
        toast.error("Failed to delete LLM model config.");
        console.error("Failed to delete LLM model config:", error);
      } finally {
        setIsConfirmOpen(false);
        setConfigToDelete(null);
      }
    }
  };

  const handleSave = async (config: LLMModelConfig) => {
    if (!token) return;
    try {
      if (selectedConfig) {
        await updateLLMModelConfig(token, config);
        toast.success("LLM model config updated successfully.");
      } else {
        await createLLMModelConfig(token, config);
        toast.success("LLM model config created successfully.");
      }
      fetchConfigs();
    } catch (error) {
      toast.error("Failed to save LLM model config.");
      console.error("Failed to save LLM model config:", error);
    } finally {
      setIsModalOpen(false);
      setSelectedConfig(null);
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <Header />
      <main className="flex-1 overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-semibold">LLM Model Configs</h1>
          <Button onClick={handleCreate}>Create New Config</Button>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Hosting Type</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell>{config.id}</TableCell>
                  <TableCell>{config.name}</TableCell>
                  <TableCell>{config.hosting_type}</TableCell>
                  <TableCell>{config.endpoint}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(config)}>Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(config)} className="text-red-600">
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      <LLMConfigModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} config={selectedConfig} />

      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="Delete LLM Model Config"
        description={`Are you sure you want to delete the config "${configToDelete?.name}"? This action cannot be undone.`}
      />
    </div>
  );
};
